import cx from 'classnames';
import React, { useState } from 'react';
import { RD_COMPONENTS } from '../_utils';
import { Button } from '../button';
import { Icon, IconColor, IconID, IconSize } from '../icon';
import { Horizontal } from '../layout';
import styles from './form-control.module.scss';
export const FormControl = ({ label, labelClassname, children, className, helpText = '', iButtonHelpText, fullWidth = false, hasError = false, errorMessage = '', disabled = false, layout = 'vertical', readOnly = false, noMarginBottom = false, errorInsteadOfLabel = false, ...restProps }) => {
    const [hasFocus, setHasFocus] = useState(false);
    if (!React.isValidElement(children)) {
        // eslint-disable-next-line no-console
        console.warn('Children should be a valid react element');
        return null;
    }
    const containerClassNames = cx(className, styles.container, {
        [styles.hasError]: hasError,
        [styles.hasFocus]: hasFocus,
        [styles.fullWidth]: fullWidth,
        [styles.horizontalLayout]: layout === 'horizontal',
        [styles.noMarginBottom]: noMarginBottom,
    });
    const formItem = children;
    const formItemName = formItem.props.name;
    const formItemId = formItemName ? `form-item-${formItemName}` : undefined;
    const helperTextId = formItemId && helpText ? `helper-text-${formItemName}` : undefined;
    const getErrorMarkup = (message) => {
        const errorClassName = cx(styles.helpText, {
            [styles.hasError]: hasError,
            [styles.disabled]: disabled,
        });
        if (message) {
            return (React.createElement("div", { "data-id": helperTextId, className: errorClassName },
                hasError && (React.createElement("span", { className: styles.iconStyle },
                    React.createElement(Icon, { color: IconColor.TEXT_COLOR, size: IconSize.XSMALL, id: IconID.EXCLAMATION_POINT_CIRCLE }))),
                message));
        }
        return null;
    };
    const getHelpTextMarkup = () => {
        // Don't show help text below the component when errorInsteadOfLabel is true
        if (errorInsteadOfLabel) {
            return null;
        }
        const helpContent = hasError && errorMessage ? errorMessage : helpText;
        return getErrorMarkup(helpContent);
    };
    const inputBlur = formItem.props.onBlur;
    const handleBlur = (event) => {
        if (inputBlur) {
            inputBlur(event);
        }
        setHasFocus(false);
    };
    const inputFocus = formItem.props.onFocus;
    const handleFocus = (event) => {
        if (inputFocus) {
            inputFocus(event);
        }
        setHasFocus(true);
    };
    const selectToggle = formItem.props.onMenuToggle;
    const handleToggle = (visible) => {
        if (selectToggle) {
            selectToggle(visible);
        }
        setHasFocus(visible);
    };
    const isValidInput = () => {
        const elementType = children.type;
        return [
            RD_COMPONENTS.TEXTAREA,
            RD_COMPONENTS.INPUT_NUMBER,
            RD_COMPONENTS.INPUT_TEXT,
            RD_COMPONENTS.INPUT_FILE,
        ].includes(elementType.RD_COMPONENT);
    };
    const isValidSelect = () => {
        const elementType = children.type;
        return [RD_COMPONENTS.DROPDOWN].includes(elementType.RD_COMPONENT);
    };
    const getChildrenProps = () => {
        /** This is added to remove double error text
         * as Textarea internally has error text
         */
        const showError = children.type.RD_COMPONENT !== RD_COMPONENTS.TEXTAREA
            ? hasError
            : false;
        const childrenDefaultProps = {
            id: children.props.id || formItemId,
            'aria-describedby': helperTextId,
            ...(isValidInput() && {
                hasError: showError,
                onBlur: handleBlur,
                onFocus: handleFocus,
            }),
            ...(isValidSelect() && {
                hasError: showError,
                onMenuToggle: handleToggle,
            }),
        };
        return childrenDefaultProps;
    };
    const renderChildren = () => React.cloneElement(children, getChildrenProps());
    const labelClassName = cx(styles.label, labelClassname, {
        [styles.disabled]: disabled,
        [styles.horizontalLayoutLabel]: layout === 'horizontal',
        [styles.readOnly]: readOnly,
    });
    const renderLabel = () => {
        if (errorInsteadOfLabel && hasError && errorMessage) {
            return getErrorMarkup(errorMessage);
        }
        const helpTextClassName = cx(styles.helpText, {
            [styles.hasError]: hasError,
            [styles.disabled]: disabled,
        });
        return (!!label && (React.createElement(Horizontal, { spacing: "a", vAlignContent: "center", className: cx(styles.labelWrapper, {
                [styles.horizontalLayoutLabelWrapper]: layout === 'horizontal',
            }) },
            React.createElement("label", { htmlFor: formItemId, className: labelClassName },
                label,
                errorInsteadOfLabel && helpText && (React.createElement("span", { className: helpTextClassName },
                    ' ',
                    "(",
                    helpText,
                    ")"))),
            iButtonHelpText && (React.createElement(Button, { type: "tertiary", size: "xs", icon: IconID.INFORMATION_CIRCLE, iconColor: IconColor.GRAY, "data-tooltip-content": iButtonHelpText })))));
    };
    return (React.createElement("div", { className: containerClassNames, ...restProps, "aria-live": "assertive", "aria-atomic": "true" },
        renderLabel(),
        React.createElement("div", { className: layout === 'horizontal' ? styles.horizontalLayoutInput : '' },
            renderChildren(),
            getHelpTextMarkup())));
};
//# sourceMappingURL=form-control.js.map